<template>
  <div :class="[isFirst || isCollapsed ? 'is-collapsed' : '', 'sidebar']">
    <a-layout-sider
      v-model="isCollapsed"
      collapsible
      :width="sidebarWidth"
      :collapsed-width="80"
      :collapsed="isCollapsed && !settings.isMobileView"
      :class="[$style.menu, { [$style.isScrolling]: isScrolling }]"
      :style="siderStyle"
      @scroll.native="handleScroll"
    >
      <div v-if="isCollapsed" class="text-center">
        <img
          src="@/assets/images/logo_small.png"
          alt="Powerbiz"
        />
      </div>
      <img
        v-else
        src="@/assets/images/logo.svg"
        :class="[$style.logo]"
        alt="Powerbiz"
        draggable="false"
      />

      <a-skeleton
        active
        :title="false"
        :loading="isLoading"
        :paragraph="{ rows: 10, width: '100%' }"
        :class="{ 'mt-4': isLoading }"
      >
        <a-menu
          force-sub-menu-render
          :mode="'inline'"
          :selected-keys="selectedMenu"
          :open-keys="openKeys"
          :class="$style.navigation"
          :inline-indent="9"
          :style="{
            paddingBottom: '15rem',
          }"
          @click="handleClick"
          @openChange="handleOpenChange"
        >
          <!-- === For Default Menu === -->
          <template v-for="item in menuData">
            <!-- Grouping Menu -->
            <a-menu-item :key="item.key" class="group-menu">
              {{ item.title }}
            </a-menu-item>
            <template v-for="menu in item.children">
              <item
                v-if="!menu.children && !menu.category"
                :key="menu.key"
                :menu-info="menu"
              />
              <sub-menu
                v-if="menu.children"
                :key="menu.key"
                :menu-info="menu"
              />
            </template>
          </template>

          <!-- === For List Store or channel === -->
          <template
            v-if="$route.query['business_id'] || $route.query['warehouse_id']"
          >
            <a-menu-item
              v-if="permission.includes('WRITE') && !isDMSOffline"
              key="add-store"
              class="store mt-4"
            >
              <router-link
                v-if="
                  $route.query['business_id'] || $route.query['warehouse_id']
                "
                :to="to()"
                class="wrap-btn mb-4"
              >
                <a-icon type="plus-square" style="font-size: 18px" />
                {{
                  isWarehouse
                    ? $t(`sideBar.add_warehouse`)
                    : $t(`sideBar.add_store`)
                }}
              </router-link>
            </a-menu-item>
            <a-menu-item key="search-store" class="store-search">
              <div class="wrap-input-search">
                <input
                  v-model="valSearch"
                  type="text"
                  :placeholder="
                    isWarehouse
                      ? $t(`sideBar.type_warehouse_name`)
                      : $t(`sideBar.type_shop_name`)
                  "
                />
                <a-icon type="search" />
              </div>
            </a-menu-item>
            <!-- Looping channel or store -->
            <template v-if="channelData">
              <template v-for="menu in channelData">
                <item
                  v-if="!menu.children && !menu.category"
                  :key="menu.key"
                  :menu-info="menu"
                  :selected-keys="selectedMenu"
                />
                <sub-menu
                  v-if="menu.children"
                  :key="menu.key"
                  :selected-keys="selectedMenu"
                  :menu-info="menu"
                />
              </template>
            </template>
            <template v-if="isFetch && (!channelData || !channelData.length)">
              {{ $t(`sideBar.no_shop`) }}
            </template>
          </template>
        </a-menu>
      </a-skeleton>
    </a-layout-sider>
    <div
      v-if="!settings.isMobileView"
      :class="[$style.resizeHandle, { [$style.isResizing]: isResizing }]"
      role="separator"
      aria-orientation="vertical"
      aria-label="Ubah lebar sidebar"
      @mousedown="startResize"
    />
  </div>
</template>

<script>
import { getCurrentInstance } from 'vue'

import SubMenu from './partials/submenu'
import Item from './partials/item'

import dataItemMenu from './composables/item'
import { eventBusMixin } from '@/utils/mixins/eventBusMixins'

export default {
  name: 'MenuLeft',
  components: {
    SubMenu,
    Item,
  },
  mixins: [eventBusMixin],
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const instance = getCurrentInstance().proxy
    const { getQueryParam } = dataItemMenu(props)
    const to = () => {
      const route = { path: '/add-store' }
      route.query = {
        ...to.query,
        ...getQueryParam({ ...instance.$route.query }),
      }

      return route
    }

    return { to }
  },
  data() {
    return {
      isCollapsed: this.collapsed,
      isFirst: window.screen.width < 800 ? true : false,
      sidebarWidth: Number(this.$store.state.settings.leftMenuWidth) || 260,
      isResizing: false,
      isScrolling: false,
      scrollTimeout: null,
      resizeStartX: 0,
      resizeStartWidth: 0,
      valSearch: '',
      isFetch: false,
      list: [
        'channel-fulfillment',
        'channel-sales',
        'channel-product',
        'channel-promotion',
        'channel-customer',
      ],
    }
  },
  computed: {
    minSidebarWidth() {
      return 260
    },
    maxSidebarWidth() {
      return 400
    },
    siderStyle() {
      return {
        transition: this.isResizing
          ? 'none'
          : 'width 220ms cubic-bezier(0.2, 0, 0, 1), min-width 220ms cubic-bezier(0.2, 0, 0, 1), max-width 220ms cubic-bezier(0.2, 0, 0, 1), flex 220ms cubic-bezier(0.2, 0, 0, 1)',
      }
    },
    isDMSOffline() {
      return this.$store.getters['user/isDMSOffline']
    },
    isLoading() {
      return this.$store.state.user.loading
    },
    settings() {
      return this.$store.state.settings
    },
    openKeys() {
      return this.$store.state.settings.openedMenu
    },
    isWarehouse() {
      return this.$store.state.user.restriction_base === 'Warehouse'
    },
    menuData() {
      return this.$store.getters['user/menuData']
    },
    channelData() {
      return this.$store.getters.channelData(this.valSearch)
    },
    setSelectedMenu() {
      return this.$store.getters.setSelectedMenu
    },
    selectedMenu() {
      return this.$store.state.settings.selectedMenu
    },
    permission() {
      return this.$store.getters['user/can']('store-list')
    },
  },
  watch: {
    setSelectedMenu(value) {
      this.$store.commit('settings/CHANGE_SETTING', {
        setting: 'selectedMenu',
        value,
      })
    },
    collapsed(value) {
      this.isCollapsed = value
      this.isFirst = false
    },
    'settings.leftMenuWidth'(value) {
      if (!this.isResizing) {
        this.sidebarWidth = Math.min(this.maxSidebarWidth, Math.max(this.minSidebarWidth, Number(value) || this.minSidebarWidth))
      }
    },
    isCollapsed(value) {
      this.$store.commit('settings/CHANGE_SETTING', {
        setting: 'isMenuCollapsed',
        value,
      })
      this.$emit('change', value)
    },
    'settings.isMenuCollapsed'(value) {
      if (!this.settings.isMobileView) this.isCollapsed = value
    },
  },
  beforeDestroy() {
    this.stopResize()
    clearTimeout(this.scrollTimeout)
  },
  mounted() {
    this.listenBus('sidebar-channel:refresh', this._onSidebarChannelRefresh)
  },
  methods: {
    startResize(event) {
      this.isResizing = true
      this.resizeStartX = event.clientX
      this.resizeStartWidth = this.sidebarWidth
      document.body.classList.add('sidebar-resizing')
      document.addEventListener('mousemove', this.resize)
      document.addEventListener('mouseup', this.stopResize)
      event.preventDefault()
    },
    resize(event) {
      if (this.isCollapsed) {
        if (event.clientX <= this.resizeStartX + 12) return

        this.isCollapsed = false
        this.resizeStartX = event.clientX
        this.resizeStartWidth = this.minSidebarWidth
      }

      const width = this.resizeStartWidth + event.clientX - this.resizeStartX
      if (width < this.minSidebarWidth - 32) {
        this.isCollapsed = true
        this.stopResize()
        return
      }
      this.sidebarWidth = Math.min(this.maxSidebarWidth, Math.max(this.minSidebarWidth, width))
    },
    stopResize() {
      if (!this.isResizing) return

      this.isResizing = false
      document.body.classList.remove('sidebar-resizing')
      document.removeEventListener('mousemove', this.resize)
      document.removeEventListener('mouseup', this.stopResize)
      this.$store.commit('settings/CHANGE_SETTING', {
        setting: 'leftMenuWidth',
        value: this.sidebarWidth,
      })
    },
    handleScroll() {
      this.isScrolling = true
      clearTimeout(this.scrollTimeout)
      this.scrollTimeout = setTimeout(() => {
        this.isScrolling = false
      }, 700)
    },
    _onSidebarChannelRefresh() {
      const routeQuery = this.$route?.query || {}
      let businessId = routeQuery.business_id
      if (!businessId && routeQuery.warehouse_id) {
        const match = (this.$store.state.user.businessList || []).find(
          (b) => b.warehouse_id === routeQuery.warehouse_id,
        )
        businessId = match?.business_id
      }
      if (!businessId) return
      this.$store.dispatch('business/LISTCHANNEL', { business_id: businessId })
    },
    handleOpenChange(openKeys) {
      this.$store.commit('settings/CHANGE_SETTING', {
        setting: 'openedMenu',
        value: openKeys ? openKeys : [],
      })
    },
    handleClick(e) {
      if (!e.key.includes('group') && e.key !== 'search-store') {
        this.$store.commit('settings/CHANGE_SETTING', {
          setting: 'selectedMenu',
          value: e.keyPath.reverse(),
        })
      }
    },
  },
}
</script>

<style lang="scss" module>
@import './style.module.scss';
</style>

<style lang="scss">
.sidebar {
  position: relative;

  .ant-layout-sider-trigger {
    left: 0;
    background: #2196f3;
  }

  .ant-layout-sider-collapsed {
    padding-right: 0 !important;
    padding-left: 0 !important;

    .group-menu, .store, .store-search {
      display: none !important;
    }

    .ant-menu-item {
      padding: 0 25px !important;
      border-radius: 0 !important;

      span {
        display: none;
      }
    }

    .ant-menu-submenu {
      .ant-menu-submenu-title {
        padding: 0 25px !important;
        border-radius: 0 !important;

        img {
          padding-top: 6px;
          padding-bottom: 6px;
        }

        div {
          display: none;
        }
      }
    }
  }
}

body.sidebar-resizing {
  cursor: col-resize;
  user-select: none;
}
</style>
